import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Circle, AlertCircle, ExternalLink } from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';
import api from '@/services/api';

interface DiscordStatus {
  linked: boolean;
  discordUsername: string | null;
  discordLinkedAt: string | null;
  discordEmailVerified: boolean | null;
  discordRoleSyncedAt: string | null;
  botConfigured: boolean;
  udyogaApproved: boolean;
  udyogaEmailVerified: boolean;
  expectedRole: string | null;
  inServer: boolean | null;
  roleAssigned: boolean | null;
  setupComplete: boolean;
  pendingSteps: string[];
  discordInviteUrl: string | null;
}

const ROLE_LABELS: Record<string, string> = {
  aspirant: 'Aspirant',
  mentor: 'Mentor',
  admin: 'Admin',
};

const SetupStep: React.FC<{
  done: boolean;
  pending?: boolean;
  label: string;
  hint?: string;
}> = ({ done, pending, label, hint }) => (
  <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-indigo-50">
    {done ? (
      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
    ) : pending ? (
      <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
    ) : (
      <Circle className="w-5 h-5 text-gray-300 flex-shrink-0 mt-0.5" />
    )}
    <div>
      <p className={`text-sm font-medium ${done ? 'text-gray-900' : 'text-gray-700'}`}>{label}</p>
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  </div>
);

const DiscordLinkCard: React.FC = () => {
  const [status, setStatus] = useState<DiscordStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [unlinking, setUnlinking] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { success, error } = useToast();

  const fetchStatus = async () => {
    try {
      const response = await api.get('/discord/status');
      if (response.data.success) {
        setStatus(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch Discord status:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  useEffect(() => {
    const discordParam = searchParams.get('discord');
    if (!discordParam) return;

    const messages: Record<string, { type: 'success' | 'error'; text: string }> = {
      success: { type: 'success', text: 'Discord account linked successfully!' },
      error: { type: 'error', text: 'Failed to link Discord account. Please try again.' },
      already_linked: {
        type: 'error',
        text: 'This Discord account is already linked to another user.',
      },
    };

    const msg = messages[discordParam];
    if (msg) {
      if (msg.type === 'success') success(msg.text);
      else error(msg.text);
      fetchStatus();
    }

    searchParams.delete('discord');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, success, error]);

  const handleConnect = () => {
    window.location.href = '/api/discord/connect';
  };

  const handleSyncRole = async () => {
    setSyncing(true);
    try {
      const response = await api.post('/discord/sync-role');
      if (response.data.success) {
        setStatus(response.data.data);
        if (response.data.data.syncSucceeded) {
          success(`${ROLE_LABELS[response.data.data.expectedRole || ''] || 'Discord'} role assigned successfully!`);
        } else if (response.data.data.inServer === false) {
          error('Join the Udyoga Pramoda Discord server first, then sync your role again.');
        } else {
          error('Could not assign role yet. Make sure you joined the server and try again.');
        }
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to sync Discord role';
      error(message);
    } finally {
      setSyncing(false);
    }
  };

  const handleUnlink = async () => {
    setUnlinking(true);
    try {
      const response = await api.post('/discord/unlink');
      if (response.data.success) {
        success('Discord account unlinked');
        await fetchStatus();
      }
    } catch (err) {
      console.error('Failed to unlink Discord:', err);
      error('Failed to unlink Discord account');
    } finally {
      setUnlinking(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="py-6 text-sm text-gray-500">Loading Discord status...</CardContent>
      </Card>
    );
  }

  const roleLabel = ROLE_LABELS[status?.expectedRole || ''] || 'Member';

  return (
    <Card className="border-indigo-200 shadow-sm bg-gradient-to-br from-indigo-50/50 to-white">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <span className="text-xl">🎮</span>
          Discord Community
        </CardTitle>
        <CardDescription className="text-sm leading-relaxed">
          Discord is our community space for <strong>job alerts</strong>,{' '}
          <strong>mentor discussions</strong>, and <strong>official announcements</strong>.
          Create a free Discord account once, connect it here, then join our server from the invite link.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-xs text-indigo-900 bg-indigo-50 border border-indigo-100 rounded-lg p-3 leading-relaxed">
          No Discord account yet?{' '}
          <a
            href="https://discord.com/register"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium underline"
          >
            Register free on Discord
          </a>
          , then return here and click Connect Discord.
        </div>

        <div className="space-y-2">
          <p className="text-sm font-semibold text-gray-800">Setup checklist</p>
          <SetupStep
            done={Boolean(status?.udyogaEmailVerified)}
            pending={!status?.udyogaEmailVerified}
            label="Udyoga Pramoda email verified"
          />
          <SetupStep
            done={Boolean(status?.udyogaApproved)}
            pending={!status?.udyogaApproved}
            label="Udyoga Pramoda account approved"
            hint={!status?.udyogaApproved ? 'An admin must approve your profile first' : undefined}
          />
          <SetupStep
            done={Boolean(status?.linked)}
            pending={Boolean(status?.udyogaApproved && !status?.linked)}
            label="Discord account connected"
            hint={!status?.linked ? 'Authorize Udyoga Pramoda to link your Discord profile' : undefined}
          />
          {status?.linked && status.discordEmailVerified === true && (
            <SetupStep done label="Discord email verified" />
          )}
          {status?.linked && status.discordEmailVerified === false && (
            <SetupStep
              done={false}
              pending
              label="Discord email verification pending"
              hint="Verify your email in Discord account settings, then reconnect Discord here"
            />
          )}
          {status?.linked && status.discordEmailVerified === null && (
            <SetupStep
              done={false}
              pending
              label="Discord email verification"
              hint="Reconnect Discord once to refresh your verification status"
            />
          )}
          <SetupStep
            done={status?.inServer === true}
            pending={status?.linked === true && status?.inServer === false}
            label="Joined Udyoga Pramoda Discord server"
            hint={
              status?.inServer === false
                ? 'Use the invite link below, then click Sync Role'
                : undefined
            }
          />
          <SetupStep
            done={status?.roleAssigned === true}
            pending={
              Boolean(
                status?.linked &&
                  status?.udyogaApproved &&
                  status?.inServer &&
                  !status?.roleAssigned
              )
            }
            label={`${roleLabel} role assigned on Discord`}
            hint={
              status?.linked && status?.inServer && !status?.roleAssigned
                ? 'Click Sync Role after joining the server'
                : undefined
            }
          />
        </div>

        {status?.setupComplete && (
          <Badge className="bg-green-100 text-green-800 px-3 py-1">
            Community setup complete
          </Badge>
        )}

        {status?.linked ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-indigo-100">
              <div>
                <p className="text-sm text-gray-500">Connected as</p>
                <p className="font-semibold text-gray-900">{status.discordUsername}</p>
                {status.discordLinkedAt && (
                  <p className="text-xs text-gray-400 mt-1">
                    Linked {new Date(status.discordLinkedAt).toLocaleDateString()}
                  </p>
                )}
              </div>
              <Badge className="bg-indigo-100 text-indigo-800">Linked</Badge>
            </div>

            <div className="flex flex-wrap gap-2">
              {status.discordInviteUrl && status.inServer !== true && (
                <Button variant="outline" asChild>
                  <a href={status.discordInviteUrl} target="_blank" rel="noopener noreferrer">
                    Join Discord Server
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </a>
                </Button>
              )}
              {status.udyogaApproved && !status.roleAssigned && (
                <Button
                  onClick={handleSyncRole}
                  disabled={syncing}
                  className="bg-[#5865F2] hover:bg-[#4752C4] text-white"
                >
                  {syncing ? 'Syncing...' : 'Sync Role'}
                </Button>
              )}
              <Button variant="outline" onClick={handleUnlink} disabled={unlinking}>
                {unlinking ? 'Unlinking...' : 'Unlink Discord'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <Button
              onClick={handleConnect}
              className="bg-[#5865F2] hover:bg-[#4752C4] text-white w-full sm:w-auto"
            >
              Connect Discord
            </Button>
            {status?.discordInviteUrl && (
              <Button variant="outline" asChild className="w-full sm:w-auto">
                <a href={status.discordInviteUrl} target="_blank" rel="noopener noreferrer">
                  Join Discord Server
                  <ExternalLink className="w-4 h-4 ml-2" />
                </a>
              </Button>
            )}
          </div>
        )}

        {status && !status.botConfigured && (
          <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded border border-amber-100">
            Discord bot is not fully configured on the server yet. Linking still works; role sync
            will apply once the bot is set up.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default DiscordLinkCard;
